import ImageIcon from "@mui/icons-material/ImageOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Checkbox,
  IconButton,
  Link,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { title } from "radash";
import { useEffect, useState } from "react";
import { formatStartFrom } from "../../../utils/settingsTabHelper";
import { REJECTION_REASONS, urlType } from "../../../utils/urlsTabsHelper";
import { untaggedEpisodeUrl } from "../utils/podcastShows";

const EMPTY_VALUE = "\u2014";

const CONTENT_RATINGS = [
  { value: 1, label: "G" },
  { value: 2, label: "PG" },
  { value: 3, label: "PG-13" },
  { value: 4, label: "R" },
  { value: 5, label: "Alcohol" },
  { value: 6, label: "Exclude Auto Placement Targeting" },
];

const UPDATE_STATUSES = [
  { value: "live", label: "Live" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_COLORS = {
  pending: "warning.main",
  live: "success.main",
  rejected: "#7A1F1F",
};

const isEmptyValue = (value) => [null, undefined, ""].includes(value);

const SelectPlaceholder = () => (
  <Typography component="span" variant="body2" color="text.secondary">
    Select
  </Typography>
);

const labelOf = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value;

const StyledTableCell = styled(TableCell)`
  max-width: 300px;
  overflow: hidden;
  word-wrap: break-word;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// Classic hyperlink blue for episode titles and CTA URLs.
const LINK_COLOR = "#0000EE";

const StyledLink = styled(Link)`
  && {
    display: inline-block;
    max-width: 100%;
    color: ${LINK_COLOR};
    text-decoration-color: ${LINK_COLOR};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
  }
`;

const CtaLink = styled(Link)`
  && {
    display: inline-block;
    max-width: 100%;
    color: ${LINK_COLOR};
    text-decoration-color: ${LINK_COLOR};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const WrappingTableCell = styled(TableCell)`
  max-width: 300px;
  overflow: hidden;
  white-space: nowrap;
`;

const wrappingTooltipSlotProps = {
  tooltip: {
    sx: { maxWidth: 360, whiteSpace: "normal" },
  },
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#1976D214",
  },
}));

const CheckboxCell = ({ checked, onCheck, value }) => {
  return (
    <TableCell>
      <Checkbox
        style={{ width: 20, height: 20 }}
        checked={checked}
        onClick={onCheck}
        value={value}
      />
    </TableCell>
  );
};

const MultiCreativeRow = ({
  onCheck,
  onSave,
  checkable = false,
  checked = false,
  urlInfo,
  loading,
  episode,
  onPreviewArtwork,
}) => {
  const {
    id,
    link,
    rejection_reason,
    status,
    url_content_rating_id,
    url_type,
  } = urlInfo;
  const [state, setState] = useState({
    id,
    link,
    rejection_reason,
    status,
    url_content_rating_id,
    url_type,
  });
  const isStale = checkable
    ? [null, undefined, ""].includes(state.url_content_rating_id) ||
      [null, undefined, "", "pending"].includes(state.status) ||
      ([null, undefined, ""].includes(state.rejection_reason) &&
        state.status === "rejected")
    : (url_content_rating_id * 1 === state.url_content_rating_id * 1 &&
        status === state.status &&
        (rejection_reason ?? "") === (state.rejection_reason ?? "")) ||
      ([null, undefined, ""].includes(state.rejection_reason) &&
        state.status === "rejected");

  const handleStateChange = (newState) =>
    setState((oldState) => ({ ...oldState, ...newState }));

  useEffect(() => {
    const {
      id,
      link,
      rejection_reason,
      status,
      url_content_rating_id,
      url_type,
    } = urlInfo;
    setState({
      id,
      link,
      rejection_reason,
      status,
      url_content_rating_id,
      url_type,
    });
  }, [loading, urlInfo]);

  const contentLink = episode?.episode_link || untaggedEpisodeUrl(link) || link;
  const contentTitle = episode?.episode_title || contentLink;

  return (
    <StyledTableRow
      key={id}
      sx={{ "&:last-child td, &:last-child th": { border: 0, height: 48 } }}
    >
      {checkable && (
        <CheckboxCell
          checked={checked}
          onCheck={onCheck}
          value={id}
          sx={{ width: "20px", height: "20px", flexShrink: 0 }}
        />
      )}
      <StyledTableCell>
        <Typography
          variant="caption"
          color={episode ? "text.secondary" : "warning.main"}
          display="block"
        >
          {episode ? episode.podcastName : "Unmatched URL"}
        </Typography>
        <Tooltip title={contentTitle} slotProps={wrappingTooltipSlotProps}>
          <StyledLink href={contentLink} rel="noreferrer" target="_blank">
            {contentTitle}
          </StyledLink>
        </Tooltip>
      </StyledTableCell>
      <TableCell sx={{ padding: "6px 10px" }}>
        {episode?.artwork_url ? (
          <IconButton
            aria-label={`Preview artwork of ${contentTitle}`}
            color="primary"
            size="small"
            onClick={(e) => onPreviewArtwork(episode.artwork_url)}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        ) : (
          EMPTY_VALUE
        )}
      </TableCell>
      <TableCell sx={{ padding: "6px 10px" }}>
        {episode ? formatStartFrom(episode.start_from) : EMPTY_VALUE}
      </TableCell>
      <WrappingTableCell sx={{ padding: "6px 10px" }}>
        {episode ? (
          <>
            <Typography variant="body2" display="block">
              {episode.cta_player}
            </Typography>
            <Tooltip
              title={episode.cta_link}
              slotProps={wrappingTooltipSlotProps}
            >
              <CtaLink
                href={episode.cta_link}
                rel="noreferrer"
                target="_blank"
                underline="always"
              >
                {episode.cta_link}
              </CtaLink>
            </Tooltip>
          </>
        ) : (
          EMPTY_VALUE
        )}
      </WrappingTableCell>
      <TableCell sx={{ padding: "6px 10px" }}>{urlType(url_type)}</TableCell>
      <TableCell
        sx={{
          padding: "6px 10px 6px 10px",
          color: STATUS_COLORS[status] ?? "text.primary",
        }}
      >
        {title(status)}
      </TableCell>
      <TableCell sx={{ width: 285, padding: "6px 10px 6px 10px" }}>
        <Select
          disabled={status === "live"}
          size="small"
          fullWidth
          sx={{ height: 32, fontSize: 14 }}
          onChange={(e) =>
            handleStateChange({ url_content_rating_id: e.target.value })
          }
          value={state.url_content_rating_id ?? ""}
          displayEmpty
          renderValue={(value) =>
            isEmptyValue(value) ? (
              <SelectPlaceholder />
            ) : (
              labelOf(CONTENT_RATINGS, value * 1)
            )
          }
        >
          {CONTENT_RATINGS.map(({ value, label }) => (
            <MenuItem key={value} sx={{ fontSize: 14 }} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell sx={{ width: 110, padding: "6px 10px 6px 10px" }}>
        <Select
          size="small"
          fullWidth
          sx={{ height: 32, fontSize: 14 }}
          onChange={(e) => {
            const currentStatus = e.target.value;

            handleStateChange({
              status: currentStatus,
              ...(currentStatus === "live" && { rejection_reason: "" }),
            });
          }}
          value={state.status !== "pending" ? state.status : ""}
          displayEmpty
          renderValue={(value) =>
            isEmptyValue(value) ? (
              <SelectPlaceholder />
            ) : (
              labelOf(UPDATE_STATUSES, value)
            )
          }
        >
          {UPDATE_STATUSES.map(({ value, label }) => (
            <MenuItem key={value} sx={{ fontSize: 14 }} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell sx={{ padding: "6px 0px 6px 10px" }}>
        <Select
          fullWidth
          sx={{ height: 32, fontSize: 14 }}
          size="small"
          disabled={state.status !== "rejected"}
          onChange={(e) =>
            handleStateChange({ rejection_reason: e.target.value })
          }
          value={state.rejection_reason ?? ""}
          displayEmpty
          renderValue={(value) =>
            isEmptyValue(value) ? (
              <SelectPlaceholder />
            ) : (
              labelOf(REJECTION_REASONS, value)
            )
          }
        >
          {REJECTION_REASONS.map(({ value, label }) => (
            <MenuItem key={value} sx={{ fontSize: 14 }} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <IconButton
          sx={{ width: "24px", height: "24px", flexShrink: 0 }}
          onClick={(e) => onSave(state)}
          disabled={isStale}
          color={"primary"}
        >
          <SaveIcon />
        </IconButton>
      </TableCell>
    </StyledTableRow>
  );
};

export default MultiCreativeRow;
